import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import { AnalysisSubmenu } from './AnalysisSubmenu';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { useUIStore } from '../../stores/uiStore';
import { MoreSubmenu } from './MoreSubmenu';
import { ModelAutomationSubmenu } from './ModelAutomationSubmenu';
import { ModelRequestsSubmenu } from './ModelRequestsSubmenu';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  has_inbox?: boolean;
}

const navItemsConfig: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
];

export const Sidebar: React.FC = () => {
  const { sidebarCollapsed: collapsed, toggleSidebar } = useUIStore();
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { currentOrganization, organizations, switchOrganization, refreshOrganizations, isLoading: orgLoading } = useOrganization();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  // Handle swipe gestures
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartX.current) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchEndX - touchStartX.current;
      
      // Swipe right to open
      if (diff > 50 && !isMobileOpen) {
        setIsMobileOpen(true);
      }
      // Swipe left to close
      else if (diff < -50 && isMobileOpen) {
        setIsMobileOpen(false);
      }
      
      touchStartX.current = null;
    };

    if (window.innerWidth < 1024) { // Only on mobile
      window.addEventListener('touchstart', handleTouchStart);
      window.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isMobileOpen]);

  const handleOrgChange = async (orgId: number) => {
    setShowOrgDropdown(false);
    await switchOrganization(orgId);
  };

  return (
    <div>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-primary-600 text-white lg:hidden"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      {/* Main Sidebar */}
      <div
        className={clsx(
          'fixed left-0 top-0 h-full text-white transition-all duration-300 z-50 flex flex-col',
          'shadow-[2px_0_20px_rgba(0,0,0,0.15)]',
          collapsed ? 'w-[70px]' : 'w-64',
          // Mobile styles
          'lg:translate-x-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{
          background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <>
        <div className={clsx(
          'relative transition-all duration-300',
          collapsed ? 'min-h-[90px]' : 'min-h-[140px]'
        )}
      >
        {/* Logo Container */}
        <div className="flex items-center justify-center h-full">
          <img
            src="/images/u7-logo-white.png"
            alt="Umbrella 7"
            className={clsx(
              'transition-all duration-300',
              collapsed ? 'w-[50px] h-[50px]' : 'w-[100px] h-[100px]'
            )}
          />
        </div>
        
        {/* Toggle Button - Positioned below logo when collapsed */}
        <button
          onClick={toggleSidebar}
          className={clsx(
            'absolute transition-all duration-300',
            'p-1.5 rounded-lg hover:bg-white/10',
            'hidden lg:block',
            collapsed ? 'bottom-2 left-1/2 -translate-x-1/2' : 'top-4 right-4'
          )}
        >
          {collapsed ? (
            <ChevronRightIcon className="w-5 h-5" />
          ) : (
            <ChevronLeftIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {organizations.length > 0 && currentOrganization && (
        <div className="relative px-4 py-3 border-b border-white/10">
          <button
            onClick={() => setShowOrgDropdown(!showOrgDropdown)}
            className={clsx(
              'w-full flex items-center justify-between p-2 rounded-lg hover:bg-white/10 transition-colors',
              collapsed && 'justify-center'
            )}
          >
            <div className="flex items-center gap-2 overflow-hidden">
              {currentOrganization.logo_url ? (
                <img 
                  src={currentOrganization.logo_url} 
                  alt={currentOrganization.name}
                  className="w-6 h-6 rounded object-cover flex-shrink-0"
                />
              ) : (
                <BuildingOffice2Icon className="w-6 h-6 flex-shrink-0" />
              )}
              {!collapsed && (
                <span className="text-sm font-medium truncate">{currentOrganization.name}</span>
              )}
            </div>
            {!collapsed && (
              <ChevronDownIcon className={clsx(
                'w-4 h-4 transition-transform',
                showOrgDropdown && 'rotate-180'
              )} />
            )}
          </button>

          {/* Dropdown */}
          {showOrgDropdown && !collapsed && (
            <div className="absolute top-full left-4 right-4 mt-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden z-50">
              {/* Refresh button */}
              <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
                <span className="text-xs text-gray-400">Organizations</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Refreshing organizations...');
                    refreshOrganizations();
                  }}
                  className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
                  disabled={orgLoading}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {orgLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleOrgChange(org.id)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-700 transition-colors text-left',
                    org.id === currentOrganization.id && 'bg-gray-700'
                  )}
                >
                  {org.logo_url ? (
                    <img 
                      src={org.logo_url} 
                      alt={org.name}
                      className="w-5 h-5 rounded object-cover"
                    />
                  ) : (
                    <BuildingOffice2Icon className="w-5 h-5" />
                  )}
                  <span className="text-sm truncate">{org.name}</span>
                  {org.id === currentOrganization.id && (
                    <div className="ml-auto w-2 h-2 bg-primary-600 rounded-full"></div>
                  )}
                </button>
              ))}
              {organizations.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-gray-400">
                  No organizations found. Please refresh.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItemsConfig.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200',
                  isActive && 'bg-white/10 text-white border-l-4 border-white/60',
                  collapsed && 'justify-center px-0'
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <Icon className={clsx(
                'w-5 h-5 flex-shrink-0 transition-transform duration-200',
                'group-hover:translate-x-0.5',
                collapsed && 'mx-auto'
              )} />
              {!collapsed && <span className="ml-3">{item.label}</span>}
            </NavLink>
          );
        })}

        {/* Model Requests Submenu */}
        <ModelRequestsSubmenu
          collapsed={collapsed}
          onMobileClose={() => setIsMobileOpen(false)}
        />

        {/* WRP */}
        <NavLink
          to="/wrp"
          onClick={() => setIsMobileOpen(false)}
          className={({ isActive }) =>
            clsx(
              'group flex items-center px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200',
              isActive && 'bg-white/10 text-white'
            )
          }
        >
          <CalculatorIcon className={clsx(
            'w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5',
            collapsed && 'mx-auto'
          )} />
          {!collapsed && <span className="ml-3">WRP</span>}
        </NavLink>

        {/* Analysis Submenu */}
        <AnalysisSubmenu
          collapsed={collapsed}
          onMobileClose={() => setIsMobileOpen(false)}
        />

        {/* Model Automation Submenu */}
        <ModelAutomationSubmenu
          collapsed={collapsed}
          onMobileClose={() => setIsMobileOpen(false)}
        />
        
        {/* More Submenu */}
        <MoreSubmenu 
          collapsed={collapsed} 
          onMobileClose={() => setIsMobileOpen(false)} 
        />
      </nav>

      {/* User Section */}
      {user && (
        <div className="border-t border-white/10">
          {/* Account Link */}
          <NavLink
            to="/account"
            className={({ isActive }) =>
              clsx(
                'group flex items-center px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200',
                isActive && 'bg-white/10 text-white',
                collapsed && 'justify-center px-0'
              )
            }
            title={collapsed ? 'Account' : undefined}
          >
            <UserCircleIcon className={clsx(
              'w-5 h-5 flex-shrink-0 transition-transform duration-200',
              'group-hover:translate-x-0.5',
              collapsed && 'mx-auto'
            )} />
            {!collapsed && <span className="ml-3">Account</span>}
          </NavLink>
          
          {/* User Info and Logout */}
          <div className={clsx('p-4', collapsed && 'px-2')}>
            <div className={clsx('flex items-center', collapsed && 'justify-center')}>
              {!collapsed && (
                <div className="flex-1">
                  <p className="text-sm font-medium text-white/90">{user.name}</p>
                  <p className="text-xs text-white/60">{user.email}</p>
                </div>
              )}
            </div>
            <button
              onClick={logout}
              className={clsx(
                'mt-3 flex items-center text-white/60 hover:text-white transition-colors',
                collapsed ? 'justify-center w-full' : 'text-sm'
              )}
              title={collapsed ? 'Logout' : undefined}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              {!collapsed && <span className="ml-2">Logout</span>}
            </button>
          </div>
        </div>
      )}

      {/* Footer with Umbrella Logo */}
      <div className={clsx(
        'border-t border-white/10 p-4 bg-black/10',
        'flex flex-col items-center justify-center space-y-2'
      )}>
        <img
          src="/images/umbrella-vision-text.png"
          alt="Umbrella Vision"
          className={clsx(
            'transition-all duration-300',
            collapsed ? 'w-[50px]' : 'w-[140px]',
            'h-auto'
          )}
        />
        {!collapsed && (
          <>
            <p className="text-xs text-white/60 text-center">© 2025 Umbrella Vision™</p>
            <p className="text-xs text-white/40">v1.0.0</p>
          </>
        )}
      </div>
      </>
      </div>
    </div>
  );
};
